import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Upload, Sparkles, IdCard, Users, ShieldCheck, Zap, HardHat, ChevronRight, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SAMPLE_HEADSHOTS, TESTIMONIALS, STATS, FAQ } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#ea9a6f] opacity-[0.06] rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#7dd4c5] opacity-[0.05] rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="animate-fadeUp">
              <div className="inline-flex items-center gap-2 border border-[#26262e] rounded-full px-3 py-1 text-xs text-[#c9c6bf] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7dd4c5] animate-pulse" />
                Trusted by 2,400+ field teams
              </div>
              <h1 className="serif text-5xl md:text-7xl leading-[0.95] text-white mb-6">
                Professional headshots<br />
                <span className="italic text-[#ea9a6f]">for the crews</span><br />
                behind the work.
              </h1>
              <p className="text-lg text-[#a8a49b] max-w-xl mb-8 leading-relaxed">
                Batch AI headshots for field workers, tradespeople, and drivers. Your crew snaps a selfie on their phone. You get uniform, ID-badge-ready portraits in under 60 seconds — no photo studio, no scheduling.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/sign-up">
                  <Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-12 px-7 text-base w-full sm:w-auto glow-coral">
                    Generate 2 free headshots <ArrowRight className="ml-1.5 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/app">
                  <Button variant="outline" className="border-[#2a2a30] text-white bg-transparent hover:bg-[#14141a] rounded-full h-12 px-7 text-base w-full sm:w-auto">
                    See it work
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-8 text-xs text-[#7a7770]">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#7dd4c5]" /> SOC 2 Type II</span>
                <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#7dd4c5]" /> &lt; 60s per photo</span>
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#7dd4c5]" /> 2,400+ teams</span>
              </div>
            </div>

            {/* Hero visual: sample gallery */}
            <div className="relative animate-fadeUp" style={{ animationDelay: '0.15s' }}>
              <div className="grid grid-cols-3 gap-3">
                {SAMPLE_HEADSHOTS.slice(0, 6).map((h, i) => (
                  <div key={h.id} className={`relative rounded-2xl overflow-hidden border border-[#1e1e26] bg-[#101014] ${i === 1 ? 'animate-float' : ''}`} style={{ animationDelay: `${i * 0.3}s` }}>
                    <img src={h.img} alt={h.name} className="w-full aspect-[3/4] object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 p-2.5 bg-gradient-to-t from-black/85 to-transparent">
                      <div className="text-white text-xs font-medium">{h.name}</div>
                      <div className="text-[#c9c6bf] text-[10px]">{h.role}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute -top-4 -right-4 bg-[#101014] border border-[#26262e] rounded-xl px-3 py-2 shadow-2xl">
                <div className="text-[10px] text-[#7a7770] uppercase tracking-wider">Generated</div>
                <div className="text-white text-sm font-medium">184,000+ headshots</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-[#1a1a20] bg-[#0c0c10]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="serif text-3xl text-white">{s.value}</div>
              <div className="text-xs text-[#7a7770] uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <Badge className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] rounded-full mb-4">How it works</Badge>
          <h2 className="serif text-4xl md:text-5xl text-white leading-tight">Three steps.<br />No studio, no scheduling.</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {[
            { icon: Upload, step: '01', title: 'Send one link to your crew', copy: 'Import your roster from a CSV, BambooHR, or Rippling. Every worker gets a text with a mobile-web capture link — no app to install.' },
            { icon: Sparkles, step: '02', title: 'Workers snap a selfie on-site', copy: 'A trailer, a truck cab, a break room. Our capture flow guides framing, lighting, and PPE preservation for hi-vis and hard hats.' },
            { icon: IdCard, step: '03', title: 'You get badge-ready headshots', copy: 'Uniform background, brand-consistent color, print-ready PDF. Export to your HRIS or ID badge printer in one click.' }
          ].map((f) => (
            <div key={f.step} className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6 hover:border-[#2e2e38] transition-colors">
              <div className="flex items-center justify-between mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#1a1a20] border border-[#26262e] flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#ea9a6f]" />
                </div>
                <div className="mono text-xs text-[#5a5850]">{f.step}</div>
              </div>
              <h3 className="text-white text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-[#8a877f] leading-relaxed">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature block */}
      <section className="bg-[#0c0c10] border-y border-[#1a1a20]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Badge className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] rounded-full mb-4">Built for field ops</Badge>
            <h2 className="serif text-4xl md:text-5xl text-white leading-tight mb-6">The rest of the internet builds headshot tools for LinkedIn.<br /><span className="italic text-[#ea9a6f]">We built one for the jobsite.</span></h2>
            <ul className="space-y-4 text-[#c9c6bf]">
              {[
                'Hi-vis, hard hat, and uniform preservation — not erased or replaced.',
                'One admin dashboard for 25 to 25,000 workers.',
                'HRIS + SCIM sync with BambooHR, Rippling, and Workday.',
                'ID badge exports: 2x2, 35x45mm, DOT medical card, custom.',
                'SOC 2 Type II, GDPR DPA, and configurable selfie retention.'
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <ChevronRight className="w-5 h-5 text-[#ea9a6f] shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              {SAMPLE_HEADSHOTS.slice(4, 8).map((h) => (
                <div key={h.id} className="rounded-2xl overflow-hidden border border-[#1e1e26] bg-[#101014]">
                  <img src={h.img} alt={h.name} className="w-full aspect-[4/5] object-cover" />
                  <div className="p-3">
                    <div className="text-white text-sm font-medium">{h.name}</div>
                    <div className="text-[#7a7770] text-xs">{h.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <Badge className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] rounded-full mb-4">Trusted by field teams</Badge>
          <h2 className="serif text-4xl md:text-5xl text-white leading-tight">People Ops teams stopped chasing photos.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.id} className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
              <div className="flex gap-1 mb-3">
                {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#ea9a6f] text-[#ea9a6f]" />)}
              </div>
              <p className="text-[#e8e6e1] leading-relaxed mb-4">“{t.quote}”</p>
              <div className="dashed-divider mb-3" />
              <div className="text-sm text-white font-medium">{t.name}</div>
              <div className="text-xs text-[#7a7770]">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-20">
        <h2 className="serif text-4xl md:text-5xl text-white text-center mb-10">Questions from HR & Field Ops</h2>
        <Accordion type="single" collapsible className="space-y-2">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="bg-[#101014] border border-[#1e1e26] rounded-xl px-5">
              <AccordionTrigger className="text-white text-left hover:no-underline py-4">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#a8a49b] pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Big CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a1611] to-[#100e0b] border border-[#2a2418] rounded-3xl p-10 md:p-16 text-center">
          <div className="absolute inset-0 grid-bg opacity-40" />
          <div className="relative">
            <HardHat className="w-10 h-10 text-[#ea9a6f] mx-auto mb-5" />
            <h2 className="serif text-4xl md:text-6xl text-white leading-tight max-w-3xl mx-auto">Ship badges this week. Not next quarter.</h2>
            <p className="text-[#a8a49b] mt-4 max-w-xl mx-auto">Start with 2 free headshots. Onboard your first crew in an afternoon.</p>
            <Link to="/sign-up">
              <Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-12 px-8 mt-8 glow-coral">
                Create free account <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            </Link>
            <div className="mt-8 flex items-center justify-center gap-2 text-xs text-[#7a7770]">
              <Link to="/compare" className="underline hover:text-white">Comparing tools?</Link> <span>See how CrewFace stacks up.</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
